import React, { useEffect, useRef } from 'react';
import { useScroll, useSpring } from 'framer-motion';
export function ScrollLine() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    scrollYProgress
  } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 40,
    damping: 25
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    let time = 0;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const progress = smoothProgress.get();
      const lineX = w - 48;
      // Stop the line at 85% of the viewport height so it points to the contact widget
      const maxLineEnd = h * 0.85;
      const lineEnd = Math.min(h * (progress * 1.2), maxLineEnd);
      // === Main vertical line — visible, 1px ===
      if (lineEnd > 0) {
        const grad = ctx.createLinearGradient(lineX, 0, lineX, maxLineEnd);
        grad.addColorStop(0, 'rgba(255,255,255,0.15)');
        grad.addColorStop(0.3, 'rgba(255,255,255,0.1)');
        grad.addColorStop(0.7, 'rgba(255,255,255,0.08)');
        grad.addColorStop(1, 'rgba(255,255,255,0.03)');
        ctx.beginPath();
        ctx.moveTo(lineX, 0);
        ctx.lineTo(lineX, lineEnd);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.stroke();
        // Glow line behind
        ctx.beginPath();
        ctx.moveTo(lineX, 0);
        ctx.lineTo(lineX, lineEnd);
        ctx.strokeStyle = 'rgba(255,255,255,0.03)';
        ctx.lineWidth = 6;
        ctx.stroke();
      }
      // === Leading dot — bright, at the tip of the line ===
      if (progress > 0.01) {
        const tipY = lineEnd;
        const tipPulse = Math.sin(time * 3) * 0.3 + 0.7;
        // Glow
        const tipGlow = ctx.createRadialGradient(lineX, tipY, 0, lineX, tipY, 20);
        tipGlow.addColorStop(0, `rgba(255,255,255,${0.15 * tipPulse})`);
        tipGlow.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.beginPath();
        ctx.arc(lineX, tipY, 20, 0, Math.PI * 2);
        ctx.fillStyle = tipGlow;
        ctx.fill();
        // Dot
        ctx.beginPath();
        ctx.arc(lineX, tipY, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.4 * tipPulse})`;
        ctx.fill();
      }
      // === Section nodes ===
      const nodes = [0.1, 0.25, 0.42, 0.58, 0.72];
      nodes.forEach((nodePos, i) => {
        if (progress < nodePos - 0.02) return;
        const ny = maxLineEnd * nodePos;
        const nodeAlpha = Math.min(1, (progress - nodePos + 0.02) * 12);
        const pulse = Math.sin(time * 1.2 + i * 1.5) * 0.3 + 0.7;
        // Outer ring
        ctx.beginPath();
        ctx.arc(lineX, ny, 10 + pulse * 3, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${0.08 * nodeAlpha * pulse})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        // Inner dot
        ctx.beginPath();
        ctx.arc(lineX, ny, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.2 * nodeAlpha})`;
        ctx.fill();
        // Horizontal lines extending left
        const tickLen = 20 + Math.sin(time * 0.8 + i) * 8;
        ctx.beginPath();
        ctx.moveTo(lineX - tickLen, ny);
        ctx.lineTo(lineX - 6, ny);
        ctx.strokeStyle = `rgba(255,255,255,${0.06 * nodeAlpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        // Small label-like marks on right
        ctx.beginPath();
        ctx.moveTo(lineX + 6, ny);
        ctx.lineTo(lineX + 12, ny);
        ctx.strokeStyle = `rgba(255,255,255,${0.04 * nodeAlpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });
      // === Bottom swirl — points toward the contact widget ===
      if (progress > 0.65) {
        const swirlProgress = Math.min(1, (progress - 0.65) / 0.3);
        const eased = swirlProgress * swirlProgress * (3 - 2 * swirlProgress);
        const startY = maxLineEnd;
        const endX = lineX - 20; // Swirls slightly left
        const endY = maxLineEnd + 40; // And down
        // Draw the curved swirl line
        ctx.beginPath();
        ctx.moveTo(lineX, startY);
        ctx.bezierCurveTo(lineX, startY + 20, endX, endY - 20, endX, endY);
        ctx.strokeStyle = `rgba(255,255,255,${0.15 * eased})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        // Swirl endpoint dot
        const pulse = Math.sin(time * 2) * 0.3 + 0.7;
        ctx.beginPath();
        ctx.arc(endX, endY, 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.4 * eased * pulse})`;
        ctx.fill();
        // Swirl rings
        for (let r = 1; r <= 3; r++) {
          const ringRadius = 8 * r * eased;
          const ringPulse = Math.sin(time * 1.5 + r) * 0.2 + 0.8;
          ctx.beginPath();
          ctx.arc(endX, endY, ringRadius * ringPulse, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,255,255,${0.08 * eased})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
      // === Floating particles ===
      for (let i = 0; i < 10; i++) {
        const baseY = i / 10 * maxLineEnd;
        if (baseY > lineEnd) continue;
        const px = lineX + Math.sin(time * 0.5 + i * 2.3) * 25;
        const py = baseY + Math.sin(time * 0.4 + i * 1.9) * 30;
        const pAlpha = 0.06 + Math.sin(time * 0.8 + i) * 0.03;
        ctx.beginPath();
        ctx.arc(px, py, 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${pAlpha})`;
        ctx.fill();
      }
      time += 0.016;
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [smoothProgress]);
  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-[2] hidden lg:block" data-id="element-343" />;
}